import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Col,
  DatePicker,
  Drawer,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
  message,
} from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  shipmentDashboardApi,
  type DashboardMetricsDTO,
  type DashboardMonitorDTO,
  type DashboardShipmentRowDTO,
} from '../api/kfc';

const { RangePicker } = DatePicker;
const { Text, Link } = Typography;

type DrillKind = 'PROBLEM' | 'OVER_THRESHOLD';

const problemStatusLabel: Record<string, string> = {
  OPEN: '待处理',
  IN_PROGRESS: '处理中',
  RESOLVED: '已解除',
};

const fmtDays = (v: number | null | undefined) =>
  v == null || Number.isNaN(v) ? '-' : `${v}`;

const ShipmentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<DashboardMetricsDTO | null>(null);
  const [monitor, setMonitor] = useState<DashboardMonitorDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(90, 'day'),
    dayjs(),
  ]);
  const [drillKind, setDrillKind] = useState<DrillKind | null>(null);
  const [drillRows, setDrillRows] = useState<DashboardShipmentRowDTO[]>([]);
  const [drillTotal, setDrillTotal] = useState(0);
  const [drillPage, setDrillPage] = useState(0);
  const [drillLoading, setDrillLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, mon] = await Promise.all([
        shipmentDashboardApi.metrics({
          from: range[0].format('YYYY-MM-DD'),
          to: range[1].format('YYYY-MM-DD'),
        }),
        shipmentDashboardApi.monitor(),
      ]);
      setMetrics(m.data);
      setMonitor(mon.data);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || '加载看板失败');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const loadDrill = useCallback(async (kind: DrillKind, page: number) => {
    setDrillLoading(true);
    try {
      const res = await shipmentDashboardApi.shipments({
        kind,
        page,
        size: 20,
      });
      const body = (res.data as { data?: typeof res.data })?.data ?? res.data;
      setDrillRows(body.content || []);
      setDrillTotal(body.totalElements || 0);
      setDrillPage(page);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || '加载下钻列表失败');
      setDrillRows([]);
      setDrillTotal(0);
    } finally {
      setDrillLoading(false);
    }
  }, []);

  const openDrill = (kind: DrillKind) => {
    setDrillKind(kind);
    loadDrill(kind, 0);
  };

  const drillTitle = useMemo(() => {
    if (drillKind === 'PROBLEM') {
      return `问题件（${drillTotal}）`;
    }
    if (drillKind === 'OVER_THRESHOLD') {
      return `停留超过阈值（${drillTotal}）`;
    }
    return '下钻';
  }, [drillKind, drillTotal]);

  const commonColumns: ColumnsType<DashboardShipmentRowDTO> = [
    {
      title: '运单号',
      dataIndex: 'shipmentId',
      width: 160,
      render: (id: string) => (
        <Link onClick={() => navigate(`/shipments/list?shipmentId=${encodeURIComponent(id)}`)}>{id}</Link>
      ),
    },
    { title: '客户', dataIndex: 'clientCode', width: 100 },
    { title: '代理', dataIndex: 'agentIdentifier', width: 100, render: (v) => v || '-' },
    {
      title: '状态',
      dataIndex: 'statusLabel',
      width: 110,
      render: (v, r) => v || r.status || '-',
    },
  ];

  const problemColumns: ColumnsType<DashboardShipmentRowDTO> = [
    ...commonColumns,
    {
      title: '问题状态',
      dataIndex: 'problemStatus',
      width: 100,
      render: (v?: string) => problemStatusLabel[v || ''] || v || '-',
    },
    {
      title: '标记时间',
      dataIndex: 'problemMarkedAt',
      width: 170,
      render: (v?: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
    },
  ];

  const overColumns: ColumnsType<DashboardShipmentRowDTO> = [
    ...commonColumns,
    {
      title: '状态变更时间',
      dataIndex: 'statusChangedAt',
      width: 170,
      render: (v?: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '停留天',
      dataIndex: 'dwellDays',
      width: 80,
      render: (v) => fmtDays(v),
    },
    {
      title: '阈值天',
      dataIndex: 'thresholdDays',
      width: 80,
      render: (v) => (v != null ? String(v) : '-'),
    },
    {
      title: '告警',
      dataIndex: 'alert',
      width: 70,
      render: () => <Tag color="error">是</Tag>,
    },
  ];

  const problemCount = monitor?.problemCount ?? 0;
  const overCount = monitor?.overThresholdCount ?? 0;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title="运单看板"
        extra={
          <Space>
            <Text type="secondary">交付样本签收日</Text>
            <RangePicker
              value={range}
              onChange={(v) => {
                if (v?.[0] && v?.[1]) setRange([v[0], v[1]]);
              }}
              allowClear={false}
            />
          </Space>
        }
        loading={loading}
      >
        <Row gutter={16}>
          <Col xs={12} md={6}>
            <Statistic title="平均交付时长（天）" value={fmtDays(metrics?.avgDays)} />
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="最大交付时长（天）" value={fmtDays(metrics?.maxDays)} />
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="最小交付时长（天）" value={fmtDays(metrics?.minDays)} />
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="P90 交付时长（天）" value={fmtDays(metrics?.p90Days)} />
          </Col>
        </Row>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          样本数：{metrics?.sampleCount ?? 0}（已签收且签收日落在所选区间）
        </Text>
      </Card>

      <Card title="状态监控" loading={loading}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Card
              size="small"
              hoverable
              onClick={() => openDrill('PROBLEM')}
              style={{
                cursor: 'pointer',
                borderColor: problemCount > 0 ? '#ffa39e' : undefined,
                background: problemCount > 0 ? '#fff1f0' : undefined,
              }}
            >
              <Statistic
                title="问题件数量"
                value={problemCount}
                valueStyle={problemCount > 0 ? { color: '#cf1322' } : undefined}
              />
              <Text type="secondary">点击查看全部问题件</Text>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card
              size="small"
              hoverable
              onClick={() => openDrill('OVER_THRESHOLD')}
              style={{
                cursor: 'pointer',
                borderColor: overCount > 0 ? '#ffd591' : undefined,
                background: overCount > 0 ? '#fff7e6' : undefined,
              }}
            >
              <Statistic
                title="停留超过阈值"
                value={overCount}
                valueStyle={overCount > 0 ? { color: '#d46b08' } : undefined}
              />
              <Text type="secondary">点击查看超阈值运单</Text>
            </Card>
          </Col>
        </Row>
      </Card>

      <Drawer
        title={drillTitle}
        open={!!drillKind}
        onClose={() => setDrillKind(null)}
        width={820}
        destroyOnClose
        zIndex={1100}
      >
        <AdaptiveTable
          loading={drillLoading}
          rowKey="shipmentId"
          dataSource={drillRows}
          columns={drillKind === 'PROBLEM' ? problemColumns : overColumns}
          locale={{ emptyText: '暂无符合条件的运单' }}
          pagination={{
            current: drillPage + 1,
            pageSize: 20,
            total: drillTotal,
            onChange: (p) => {
              if (drillKind) loadDrill(drillKind, p - 1);
            },
          }}
        />
      </Drawer>
    </Space>
  );
};

export default ShipmentDashboard;
